/**
 * PendingApplicationsPanel — PM review panel for volunteer applications.
 *
 * Shows all pending_approval assignments across all role slots on a project.
 * PM can Approve (→ active) or Reject (→ rejected) each application.
 * Displayed at the top of ProjectTeamPanel when pending count > 0.
 */

import React, { useMemo, useCallback, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { updateRoleAssignment } from "../../api/projectRoles";
import type {
  ProjectRoleSlotResponse,
  ProjectRoleAssignmentResponse,
} from "../../api/projectRoles";
import type { SecurityUserResponse } from "../../api/security";
import { mediaSrc } from "../../api/mediaUrl";
import styles from "./PendingApplicationsPanel.module.css";

interface Props {
  projectGuid: string;
  slots: ProjectRoleSlotResponse[];
  userMap: Map<string, SecurityUserResponse>;
  onActionComplete: () => void;
}

interface PendingItem {
  slot: ProjectRoleSlotResponse;
  assignment: ProjectRoleAssignmentResponse;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name.slice(0, 2) || "??").toUpperCase();
}

function profileImgUrl(user: SecurityUserResponse): string | null {
  return mediaSrc(user.profileImageUrl);
}

const PendingApplicationsPanel: React.FC<Props> = ({
  projectGuid,
  slots,
  userMap,
  onActionComplete,
}) => {
  const { t } = useAppTranslation("projectTeam");
  const [processingId, setProcessingId] = useState<string | null>(null);

  const pendingItems: PendingItem[] = useMemo(() => {
    const items: PendingItem[] = [];
    for (const slot of slots) {
      for (const a of slot.assignments || []) {
        if (a.status === "pending_approval") {
          items.push({ slot, assignment: a });
        }
      }
    }
    // Sort newest first
    items.sort((a, b) =>
      new Date(b.assignment.assignedAt).getTime() - new Date(a.assignment.assignedAt).getTime()
    );
    return items;
  }, [slots]);

  const handleApprove = useCallback(
    async (item: PendingItem) => {
      setProcessingId(item.assignment.assignmentGuid);
      try {
        await updateRoleAssignment(
          projectGuid,
          item.slot.roleSlotGuid,
          item.assignment.assignmentGuid,
          { status: "active" }
        );
        onActionComplete();
      } catch (err) {
        console.error("[PendingApplications] Approve failed:", err);
      } finally {
        setProcessingId(null);
      }
    },
    [projectGuid, onActionComplete]
  );

  const handleReject = useCallback(
    async (item: PendingItem) => {
      setProcessingId(item.assignment.assignmentGuid);
      try {
        await updateRoleAssignment(
          projectGuid,
          item.slot.roleSlotGuid,
          item.assignment.assignmentGuid,
          { status: "rejected" }
        );
        onActionComplete();
      } catch (err) {
        console.error("[PendingApplications] Reject failed:", err);
      } finally {
        setProcessingId(null);
      }
    },
    [projectGuid, onActionComplete]
  );

  if (pendingItems.length === 0) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>{t("pending.title")}</span>
        <span className={styles.badge}>{pendingItems.length}</span>
      </div>
      <div className={styles.list}>
        {pendingItems.map((item) => {
          const user = userMap.get(item.assignment.userGuid.toUpperCase());
          const isProcessing = processingId === item.assignment.assignmentGuid;
          const appliedDate = new Date(item.assignment.assignedAt).toLocaleDateString();
          return (
            <div key={item.assignment.assignmentGuid} className={styles.item}>
              {/* Avatar */}
              <div className={styles.avatar}>
                {user && profileImgUrl(user) ? (
                  <img src={profileImgUrl(user)!} alt="" className={styles.avatarImg} />
                ) : (
                  <span className={styles.avatarInitials}>
                    {user ? getInitials(user.displayName || user.name) : "??"}
                  </span>
                )}
              </div>

              {/* Info */}
              <div className={styles.info}>
                <div className={styles.userName}>
                  {user?.displayName || user?.name || t("pending.unknownUser")}
                </div>
                <div className={styles.roleName}>
                  {item.slot.roleLabel}
                  <span className={styles.appliedDate}> · {appliedDate}</span>
                </div>
                {item.assignment.notes && (
                  <div className={styles.notes}>{item.assignment.notes}</div>
                )}
              </div>

              {/* Actions */}
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.approveBtn}
                  onClick={() => handleApprove(item)}
                  disabled={isProcessing}
                >
                  {t("pending.approve")}
                </button>
                <button
                  type="button"
                  className={styles.rejectBtn}
                  onClick={() => handleReject(item)}
                  disabled={isProcessing}
                >
                  {t("pending.reject")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PendingApplicationsPanel;
